import React, { cloneElement, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Tooltip.module.css";

// the default position is 'auto' for tooltips, which tries to find the positioning that is least likely to
// clip off-screen. However, it doesn't find the positioning that is most-likely to not clip with other elements
// because that determination is complicated to do programmatically. The best default for not clipping with other
// elements is probably "top" or "left", but it obviously fails for elements on the left/top of the screen, so
// I don't really know what the actual best default is.
export type Placement = "auto" | "top" | "top-left" | "top-right" | "right" | "bottom" | "bottom-left" | "bottom-right" | "left";

export interface TooltipProps {
  title?: string | null;
  delayMs?: number,
  placement?: Placement;
  children: React.ReactElement | React.JSX.Element | string | number;
}

const oneTimeEventListener = (element: HTMLElement, eventType: string, callback: Function) => {
  const handler = () => {
    callback();
    element.removeEventListener(eventType, handler);
  }
  element.addEventListener(eventType, handler);
}

export function Tooltip(props: TooltipProps) {
  const { children, title, delayMs = 600, placement = "auto" } = props;
  const [tooltip, setTooltip] = useState(null);
  const [visible, setVisible] = useState(false);
  const [renderTooltip, setRenderTooltip] = useState(false);
  const targetRef = useRef(null);
  const tooltipRef = useRef(null);

  const targetElement = ref => {
    if (ref.current && getComputedStyle(ref.current).display === 'contents') {
      // "display: contents" allows us to have a ref to a parent element for function components without disrupting
      // layout and style, but it also causes the element to have no position or size. So we need to grab the child
      // element instead for such purposes. (This won't work for function components that don't return a single root
      // HTML element. I have no idea how we could support that.)
      return ref.current.firstChild;
    }
    return ref.current;
  };

  useEffect(() => {
    if (!targetRef.current) return;
    const onLeave = timer => () => {
      clearTimeout(timer);
      setVisible(false);
      if (tooltipRef.current) {
        oneTimeEventListener(tooltipRef.current, 'transitionend', () => setRenderTooltip(false));
      }
    };
    const onEnter = () => {
      const timeout = setTimeout(() => {
        setVisible(true);
        setRenderTooltip(true);
      }, delayMs);
      oneTimeEventListener(targetElement(targetRef), 'mouseleave', onLeave(timeout));
    };
    targetElement(targetRef).addEventListener('mouseenter', onEnter);
    return () => targetElement(targetRef)?.removeEventListener('mouseenter', onEnter);
  }, [targetRef.current]);

  useEffect(() => {
    if (!targetRef.current) {
      setTooltip(null);
      return;
    }

    const target = targetElement(targetRef);
    const locatorRect = target.getBoundingClientRect();
    const margin = 20;
    const width = `${locatorRect.width + margin + 1}px`;
    const height = `${locatorRect.height + margin + 1}px`
    const style = {
      top: `${locatorRect.top - margin/2}px`,
      left: `${locatorRect.left - margin/2}px`,
      width,
      minWidth: width,
      maxWidth: width,
      height,
      minHeight: height,
      maxHeight: height,
    };

    setTooltip(createPortal(
      (title || null) && (
        <div ref={tooltipRef} className={styles.tooltipWrapper} style={style}>
          <div
            className={`${styles.tooltip} ${visible ? styles.visible : ''}`}
            aria-label={title}
            role="tooltip"
            data-microtip-position={getElementTooltipPosition(placement, target)}
            data-microtip-size="fit"
          />
        </div>
      ),
      document.body,
    ));
  }, [targetRef.current, children, title, placement, visible]);

  // this is very irritating (this would be trivial in vanilla js), but because
  // 'function components cannot be given refs', and sometimes 'children' is JSX,
  // and other times it is function components, if and only if it is a function
  // component we need to wrap it in a div and give that a ref instead. This has
  // potential to break styling on pages, because the div won't have the same style
  // as the function component necessarily.
  //
  // if there was a way to just attach a ref/get the html element for a given react
  // node, regardless of if it is JSX or a function component, that would work, but
  // as far as I can tell, there is no way to do that that is not insanely janky...
  //
  // (Later note from James: I think the style issue is mostly resolved with
  // "display: contents", though there may be rare cases where that breaks, too, e.g.,
  // if there are selectors that style a direct descendent of some parent element)
  let wrappedChildren;
  if (typeof children === "string" || typeof children === "number") {
    wrappedChildren = <span ref={targetRef}>{children.toString()}</span>;
  } else if (typeof children.type === "function") {
    wrappedChildren = <span style={{ display: "contents" }} ref={targetRef}>{children}</span>;
  } else {
    // this clone-element business is necessary because we don't want to introduce a wrapper div
    // unless we have to (because that breaks styles all over the place, in particular with 
    // truncated text tooltips)
    wrappedChildren = cloneElement(children as React.ReactElement, { ref: targetRef });
  }

  return <>
    {wrappedChildren}
    {renderTooltip && tooltip}
  </>
}

// decompose the window into a 3x3 grid. based on the ninth that the tooltip elements fall in,
// determine positioning that is unlikely to cause the tooltip to be clipped.
//
//     .----------.----------.----------.
//     |  bottom  |  bottom  |  bottom  |
//     |   right  |          |   left   |
//     |----------+----------+----------|
//     |   right  |   top    |   left   |
//     |          |          |          |
//     |----------+----------+----------|
//     |   top    |   top    |   top    |
//     |   right  |          |   left   |
//     '----------'----------'----------'
//
// each ninth chooses a position that 'points' towards the center ninth,
// except the center ninth, which arbitrarily chooses 'top'.
// 
function getElementTooltipPosition(placement: Placement, element: HTMLElement): Omit<Placement, "auto"> {
  if (placement !== 'auto') return placement;

  const width = window.innerWidth/3;
  const height = window.innerHeight/3;
  const quads: { left: number, top: number, right: number, bottom: number }[] = [];
  for (let y = 0; y < 3; y++) {
    for (let x = 0; x < 3; x++) {
      quads.push({ left: x*width, top: y*height, right: x*width+width, bottom: y*height+height });
    }
  }
  const elementRect = element.getBoundingClientRect();
  for (let i = 0; i < quads.length; i++) {
    const quad = quads[i];
    if (
      elementRect.left <= quad.right 
      && quad.left <= elementRect.right 
      && elementRect.top <= quad.bottom 
      && quad.top <= elementRect.bottom
    ) {
      switch (i) {
        case 0: return "bottom-right";
        case 1: return "bottom";
        case 2: return "bottom-left";

        case 3: return "right";
        case 4: return "top"; // arbitrary choice here
        case 5: return "left";

        case 6: return "top-right";
        case 7: return "top";
        case 8: return "top-left";
      }
      break;
    }
  }
  // if we got here, we someone failed to find a ninth of the screen to which this element belongs.
  // it could be off-screen, or not attached to the DOM yet. you shouldn't use 'auto' with an element
  // that does this. we'll default to 'top' here and just hope we're safe:
  // console.warn("Failed to find a ninth for element connected to tooltip: ", elementRect, quads);
  return "top";
}
